import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, MapPin, ChevronDown, Mail, Phone, Navigation } from 'lucide-react';
import { Customer } from '../../lib/customers';
import AddressAutocompleteModal from '../AddressAutocompleteModal';
import CustomerMessageModal from '../CustomerMessageModal';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { useMessagingChannels } from '../../hooks/useMessagingChannels';
import { openNativeCall, openNativeSms, openNativeEmail } from '../../lib/contactActions';

import { CustomerSource, CustomerSourcesService } from '../../lib/customerSources';

interface BookingCustomerInfoProps {
  customer: Customer;
  onEdit?: () => void;
  /** Opens customer details (e.g. field quote) without requiring `canEdit` for the whole card. */
  onOpenCustomerDetails?: () => void;
  onChangeCustomer?: () => void;
  bookingAddress?: string;
  onEditBookingAddress?: (address: string, lat?: number, lng?: number) => void;
  mobile_booking?: boolean;
  canEdit?: boolean;
  customerNotes?: string;
  onEditCustomerNotes?: (notes: string) => void;
  bookingSource?: string | null;
  onSourceUpdate?: (source: string | null) => void;
  businessId?: string;
  bookingId?: string;
  onNavigateToAddress?: (address: string) => void;
  bookingLat?: number | null;
  bookingLng?: number | null;
  /** When true with mobile_booking, address card omits the lat/lng line (e.g. field quotes without geocoding). */
  hideAddressCoordinates?: boolean;
  /** Fired when the user interacts with customer actions (call, message, email, open/edit). */
  onCustomerInteraction?: () => void;
  /** Tighter layout for embedded kanban cards (same actions: call, SMS, email). */
  dense?: boolean;
  /** Optional badge rendered top-right of the name row (e.g. Active/Inactive customer). */
  statusBadge?: React.ReactNode;
}

export default function BookingCustomerInfo({ 
  customer, 
  onEdit,
  onOpenCustomerDetails,
  onChangeCustomer,
  bookingAddress,
  onEditBookingAddress,
  mobile_booking = false,
  canEdit = true,
  customerNotes,
  onEditCustomerNotes,
  bookingSource,
  onSourceUpdate,
  businessId,
  bookingId,
  onNavigateToAddress,
  bookingLat,
  bookingLng,
  hideAddressCoordinates = false,
  onCustomerInteraction,
  dense = false,
  statusBadge,
}: BookingCustomerInfoProps) {
  const hasCustomerInfoAccess = true; // No longer using permissions
  const { canAccess } = usePermissionCheck();
  const { twilioConnected, emailConnected } = useMessagingChannels();
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [tempAddress, setTempAddress] = useState('');

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [tempNotes, setTempNotes] = useState(customerNotes || '');
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false);
  const sourceButtonRef = useRef<HTMLDivElement>(null);
  
  // Customer Source state
  const [selectedCustomerSource, setSelectedCustomerSource] = useState<CustomerSource | null>(null);
  const [customerSources, setCustomerSources] = useState<CustomerSource[]>([]);
  
  // Message modal state
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageModalComposerType, setMessageModalComposerType] = useState<'sms' | 'email'>('sms');

  // Load customer sources when component mounts
  useEffect(() => {
    if (businessId) {
      loadCustomerSources();
    }
  }, [businessId]);

  // Update temp notes when customerNotes prop changes
  useEffect(() => {
    setTempNotes(customerNotes || '');
  }, [customerNotes]);

  // Function to load customer sources from database
  const loadCustomerSources = async () => {
    if (!businessId) return;
    
    try {
      const sources = await CustomerSourcesService.getCustomerSources(businessId);
      
      // Default sources that should always be available
      const defaultSources: CustomerSource[] = [
        {
          id: 'repeat-customer',
          business_id: businessId,
          name: 'Repeat Customer',
          is_active: true,
          created_at: '',
          updated_at: ''
        },
        {
          id: 'referral',
          business_id: businessId,
          name: 'Referral',
          is_active: true,
          created_at: '',
          updated_at: ''
        }
      ];
      
      // Merge default sources with database sources, avoiding duplicates
      const allSources = [...defaultSources];
      sources.forEach(source => {
        // Don't add if a default source with the same name exists
        if (!defaultSources.some(ds => ds.name.toLowerCase() === source.name.toLowerCase())) {
          allSources.push(source);
        }
      });
      
      // Sort alphabetically by name
      allSources.sort((a, b) => a.name.localeCompare(b.name));
      
      setCustomerSources(allSources);
    } catch (error) {
      console.error('Error loading customer sources:', error);
    }
  };

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    if (!isSourceDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const dropdownElement = document.querySelector('[data-source-dropdown]');
      
      // Don't close if clicking on the source button or dropdown
      if (
        sourceButtonRef.current?.contains(target) || 
        dropdownElement?.contains(target)
      ) {
        return;
      }
      setIsSourceDropdownOpen(false);
    };

    const handleScroll = () => {
      setIsSourceDropdownOpen(false);
    };

    // Use a longer delay to ensure React onClick handlers fire first
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 200);

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isSourceDropdownOpen]);





  // Set initial customer source based on booking's source
  useEffect(() => {
    if (bookingSource) {
      setSelectedCustomerSource({
        id: bookingSource,
        business_id: businessId || '',
        name: bookingSource,
        is_active: true,
        created_at: '',
        updated_at: ''
      });
    }
  }, [bookingSource, businessId]);



  const handleStartEditing = () => {
    setTempAddress(bookingAddress || '');
    setIsEditingAddress(true);
  };

  // Handle opening message modal
  const handleOpenMessageModal = (composerType: 'sms' | 'email' = 'sms') => {
    onCustomerInteraction?.();
    setMessageModalComposerType(composerType);
    setMessageModalOpen(true);
  };

  if (!hasCustomerInfoAccess) {
    return null;
  }

  return (
    <>
    <div className="relative">
      <div className="space-y-1.5">
        {/* Customer Info - iOS Style Card - Compact */}
        <div 
          className={`
            bg-white rounded-[12px] border border-gray-200/60 relative
            ${dense ? 'p-1.5' : 'p-2'}
            ${(canEdit && onEdit) || onOpenCustomerDetails ? 'cursor-pointer active:scale-[0.98] touch-manipulation' : ''}
            transition-all duration-150
            shadow-[0_1px_2px_rgba(0,0,0,0.05)]
          `}
          onClick={(e) => {
            // Stop propagation if clicking on action buttons area
            const target = e.target as HTMLElement;
            if (target.closest('.customer-action-buttons') || target.closest('button[class*="bg-green-50"]') || target.closest('button[class*="bg-red-50"]')) {
              e.stopPropagation();
              return;
            }
            if (onOpenCustomerDetails) {
              onCustomerInteraction?.();
              onOpenCustomerDetails();
              return;
            }
            if (canEdit && onEdit) {
              onCustomerInteraction?.();
              onEdit();
            }
          }}
          onMouseDown={(e) => {
            // Stop propagation if clicking on action buttons area
            const target = e.target as HTMLElement;
            if (target.closest('.customer-action-buttons') || target.closest('button[class*="bg-green-50"]') || target.closest('button[class*="bg-red-50"]')) {
              e.stopPropagation();
            }
          }}
          style={{
            WebkitTapHighlightColor: 'transparent',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
          }}
        >
          {/* Source - Top Right Corner */}
          {(canEdit && onSourceUpdate) || (selectedCustomerSource || bookingSource) ? (
            <div 
              ref={sourceButtonRef}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (canEdit && onSourceUpdate) {
                  setIsSourceDropdownOpen(!isSourceDropdownOpen);
                }
              }}
              className={`
                absolute top-2 right-2 text-[10px] text-gray-600 px-1.5 py-0.5 rounded-[6px] bg-blue-50
                ${canEdit && onSourceUpdate ? 'cursor-pointer hover:bg-blue-100 active:scale-[0.95] touch-manipulation transition-all' : ''}
                source-dropdown z-10
              `}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {selectedCustomerSource ? selectedCustomerSource.name : (bookingSource || 'Select source...')}
              {canEdit && onSourceUpdate && (
                <ChevronDown className="inline-block w-2 h-2 ml-0.5" />
              )}
            </div>
          ) : null}
          
          {/* Top row: Customer name, supplier badge, and avatar */}
          <div className={`flex items-center space-x-2 ${dense ? 'mb-1.5' : 'space-x-2.5 mb-2'}`}>
            {/* Avatar - Top Left, Smaller, Inline with name */}
            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0 uppercase ${customer.is_supplier ? 'bg-amber-500' : 'bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm'}`}>
              {customer.first_name?.[0] ?? ''}{customer.last_name?.[0] ?? ''}
            </div>
            <div className="flex-1 min-w-0 leading-tight">
              <div
                className={`font-semibold text-gray-900 truncate ${
                  dense ? 'text-[13px]' : 'text-[14px]'
                }`}
              >
                {customer.first_name} {customer.last_name}
              </div>
              {customer.company && (
                <div className={`text-gray-500 truncate ${dense ? 'text-[11px]' : 'text-[12px]'}`}>
                  {customer.company}
                </div>
              )}
            </div>
            {statusBadge && <span className="flex-shrink-0">{statusBadge}</span>}
            {customer.is_supplier && (
              <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200/80">
                Supplier
              </span>
            )}
          </div>
          
          {/* Dividing line */}
          <div className={`border-t border-gray-200/60 ${dense ? 'my-1.5' : 'my-2'}`} />

          {/* Action Icons - Below dividing line */}
          <div
            className={`customer-action-buttons relative z-10 flex items-center justify-between ${
              dense ? 'pt-0.5' : 'pt-1'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center ${dense ? 'space-x-1' : 'space-x-1.5'}`}>
            {canAccess.messages && customer.phone && (
              <>
                <button
                  className={`flex items-center justify-center border border-gray-200/60 bg-transparent text-green-600 rounded-[8px] hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 active:scale-95 transition-all duration-150 touch-manipulation relative z-10 ${
                    dense ? 'h-7 w-7' : 'h-8 w-8'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCustomerInteraction?.();
                    if (!customer.phone) return;
                    if (twilioConnected) {
                      // Close all modals first, then open the in-app dialler
                      window.dispatchEvent(new CustomEvent('closeAllModals'));
                      setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('openDialler', {
                          detail: { phoneNumber: customer.phone }
                        }));
                      }, 100);
                    } else {
                      // No Twilio — use the device's native call sheet
                      openNativeCall(customer.phone);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                    pointerEvents: 'auto',
                    zIndex: 10
                  }}
                  title="Call customer"
                >
                  <Phone className={dense ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                </button>
                <button
                  className={`flex items-center justify-center border border-gray-200/60 bg-transparent text-green-600 rounded-[8px] hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 active:scale-95 transition-all duration-150 touch-manipulation relative z-10 ${
                    dense ? 'h-7 w-7' : 'h-8 w-8'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (twilioConnected) {
                      handleOpenMessageModal('sms');
                    } else {
                      onCustomerInteraction?.();
                      openNativeSms(customer.phone);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                    pointerEvents: 'auto',
                    zIndex: 10
                  }}
                  title="Message customer"
                >
                  <MessageSquare className={dense ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                </button>
              </>
            )}
            {canAccess.messages && customer.email && (
              <button
                className={`flex items-center justify-center border border-gray-200/60 bg-transparent text-red-600 rounded-[8px] hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 active:scale-95 transition-all duration-150 touch-manipulation relative z-10 ${
                  dense ? 'h-7 w-7' : 'h-8 w-8'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!customer.email) return;
                  if (emailConnected) {
                    handleOpenMessageModal('email');
                  } else {
                    onCustomerInteraction?.();
                    openNativeEmail(customer.email);
                  }
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                }}
                style={{ 
                  WebkitTapHighlightColor: 'transparent',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                  pointerEvents: 'auto',
                  zIndex: 10
                }}
                title="Email customer"
              >
                <Mail className={dense ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              </button>
            )}
            </div>
            
            {/* Change Customer Button - Right Side */}
            {canEdit && onChangeCustomer && (
              <button
                className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChangeCustomer();
                }}
                style={{ 
                  WebkitTapHighlightColor: 'transparent',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                }}
                title="Change customer"
              >
                Change
              </button>
            )}
          </div>
        </div>
        {/* Booking Address - Mobile bookings only - iOS Style */}
        {mobile_booking && (
          <div className="flex items-stretch gap-2">
            {/* Navigate button */}
            {onNavigateToAddress && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (bookingAddress) onNavigateToAddress(bookingAddress);
                }}
                disabled={!bookingAddress}
                className={`flex-shrink-0 w-10 rounded-[12px] flex items-center justify-center transition-all duration-150 active:scale-[0.95] touch-manipulation shadow-[0_1px_2px_rgba(0,0,0,0.1)] ${
                  bookingAddress
                    ? 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
                title={bookingAddress ? 'Navigate to address' : 'No address set'}
              >
                <Navigation className={`h-4 w-4 ${bookingAddress ? 'text-white' : 'text-gray-400'}`} />
              </button>
            )}
            {/* Address text - tap to edit */}
            <div 
              className={`
                flex-1 bg-white rounded-[12px] border border-gray-200/60 p-2.5
                ${canEdit && onEditBookingAddress && !isEditingAddress ? 'cursor-pointer active:scale-[0.98] touch-manipulation' : ''}
                transition-all duration-150
                shadow-[0_1px_2px_rgba(0,0,0,0.05)]
                ${isEditingAddress ? 'overflow-visible relative z-20' : ''}
              `}
              onClick={() => {
                if (canEdit && onEditBookingAddress && !isEditingAddress) {
                  handleStartEditing();
                }
              }}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {isEditingAddress && onEditBookingAddress ? (
                <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                  <AddressAutocompleteModal
                    value={tempAddress}
                    onChange={(address) => setTempAddress(address)}
                    onSelect={(address) => {
                      onEditBookingAddress(address.formatted, address.lat, address.lng);
                      setIsEditingAddress(false);
                      setTempAddress('');
                    }}
                    onManualSave={(address, lat, lng) => {
                      onEditBookingAddress(address, lat, lng);
                      setIsEditingAddress(false);
                      setTempAddress('');
                    }}
                    placeholder="Enter booking address"
                    className="w-full h-9 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2"
                    autoFocus
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingAddress(false);
                        setTempAddress('');
                      }}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
              <div className="flex items-start space-x-2">
                <MapPin className={`h-3 w-3 flex-shrink-0 mt-0.5 ${bookingAddress ? 'text-gray-400' : 'text-gray-300'}`} />
                <div>
                  <span className={`text-[12px] leading-tight ${bookingAddress ? 'text-gray-600' : 'text-gray-400 italic'}`}>
                    {bookingAddress || 'Tap to add booking address...'}
                  </span>
                  {mobile_booking && !hideAddressCoordinates && (
                    <div className={`text-[10px] font-mono mt-0.5 ${bookingLat != null && bookingLng != null ? 'text-gray-400' : 'text-red-400'}`}>
                      {bookingLat != null && bookingLng != null
                        ? `${bookingLat.toFixed(6)}, ${bookingLng.toFixed(6)}`
                        : 'No coordinates set'}
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        )}

        {/* Customer Notes - iOS Style */}
        {(customerNotes || (canEdit && onEditCustomerNotes)) && (
          <div 
            className={`
              bg-white rounded-[12px] border border-gray-200/60 p-2.5
              ${canEdit && onEditCustomerNotes ? 'cursor-text' : ''}
              shadow-[0_1px_2px_rgba(0,0,0,0.05)]
            `}
            onClick={() => canEdit && onEditCustomerNotes && !isEditingNotes && setIsEditingNotes(true)}
          >
            {isEditingNotes && onEditCustomerNotes ? (
              <textarea
                value={tempNotes}
                onChange={(e) => setTempNotes(e.target.value)}
                onBlur={() => {
                  if (tempNotes !== customerNotes) {
                    onEditCustomerNotes(tempNotes);
                  }
                  setIsEditingNotes(false);
                }}
                placeholder="Add customer notes..."
                className="w-full h-16 rounded-[8px] bg-gray-50 border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[12px] px-2.5 py-2 resize-none"
                autoFocus
                style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}
              />
            ) : (
              <div className={`text-[12px] ${customerNotes ? 'text-gray-600' : 'text-gray-400 italic'}`}>
                {customerNotes || 'Tap to add customer notes...'}
              </div>
            )}
          </div>
        )}
      </div>



      {/* Source Dropdown Portal */}
      {isSourceDropdownOpen && sourceButtonRef.current && createPortal(
        <div 
          data-source-dropdown
          className="fixed bg-white border border-gray-200 rounded-[10px] shadow-lg z-[99999] min-w-[150px] max-w-[200px] overflow-hidden"
          style={{
            right: window.innerWidth - sourceButtonRef.current.getBoundingClientRect().right,
            top: sourceButtonRef.current.getBoundingClientRect().bottom + 6,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('No source clicked');
              if (onSourceUpdate) {
                onSourceUpdate(null);
              }
              setIsSourceDropdownOpen(false);
            }}
            className="px-3 py-2 text-[12px] hover:bg-gray-50 active:bg-gray-100 cursor-pointer text-gray-500 transition-colors touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            No source
          </div>
          {customerSources.map((source) => (
            <div
              key={source.id}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Source clicked:', source.name);
                if (onSourceUpdate) {
                  console.log('Calling onSourceUpdate with:', source.name);
                  onSourceUpdate(source.name);
                } else {
                  console.log('onSourceUpdate is not defined');
                }
                setIsSourceDropdownOpen(false);
              }}
              className={`px-3 py-2 text-[12px] hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors touch-manipulation ${
                bookingSource === source.name ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {source.name}
            </div>
          ))}
        </div>,
        document.body
      )}

    </div>
    
    {/* Message Modal */}
    <CustomerMessageModal
      isOpen={messageModalOpen}
      onClose={() => setMessageModalOpen(false)}
      customer={customer}
      businessId={businessId}
      initialComposerType={messageModalComposerType}
    />

  </>
  );
}